import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchBaseQueryWithAuth } from '../../fetchBaseQuery';
import { runtimeConfig } from '../../../config/runtimeConfig';
import type {
  CatalogSyncRequest,
  CatalogSyncResponse,
  KbCrawlRequest,
  KbCrawlResponse,
  KbStatusRequest,
  KbStatusResponse,
} from './types';

export const ellaDataLayerApi = createApi({
  reducerPath: 'ellaDataLayerApi',
  baseQuery: fetchBaseQueryWithAuth(runtimeConfig.ellaDataLayerApiBaseUrl || ''),
  tagTypes: ['KbStatus'],
  endpoints: (builder) => ({
    /**
     * Triggers a full catalog ingestion for the merchant. The data layer pages the whole
     * catalog before responding, so this can run for minutes on large catalogs.
     */
    catalogSync: builder.mutation<CatalogSyncResponse, CatalogSyncRequest>({
      query: ({ merchantId }) => ({
        url: '/catalog/sync',
        method: 'POST',
        body: { merchant_id: merchantId },
      }),
    }),
    kbStatus: builder.query<KbStatusResponse, KbStatusRequest>({
      query: ({ merchantId }) => ({
        url: '/kb/status',
        params: { merchant_id: merchantId },
      }),
      providesTags: (_result, _error, { merchantId }) => [{ type: 'KbStatus', id: merchantId }],
    }),
    /** Starts a help-center crawl and returns at once; the crawl itself takes minutes. */
    startKbCrawl: builder.mutation<KbCrawlResponse, KbCrawlRequest>({
      query: ({ merchantId, helpCenterUrls }) => ({
        url: '/kb/crawl',
        method: 'POST',
        body: { merchant_id: merchantId, help_center_urls: helpCenterUrls },
      }),
      invalidatesTags: (_result, _error, { merchantId }) => [{ type: 'KbStatus', id: merchantId }],
    }),
  }),
});

export const { useCatalogSyncMutation, useKbStatusQuery, useStartKbCrawlMutation } =
  ellaDataLayerApi;
